import type { ElementType, MouseEventHandler, ReactNode } from 'react';
import { useEffect, useState } from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import ChevronDownIcon from '../icons/ChevronDownIcon';

import { usePositionedPopup } from '../../hooks/use-positioned-popup/use-positioned-popup';
import { Box } from '../box/box';
import { Button } from '../button/button';
import { Card } from '../card/card';
import { GrandEntrance } from '../grand-entrance/grand-entrance';
import { Link } from '../link/link';
import { Text } from '../text/text';

import { TEST_IDS } from './test-ids';

import * as styles from './user-menu.css';

export interface UserMenuProps {
  email: string;
  role: string;
  logOutText: ReactNode;
  logOutHref: string;
  logOutCallback?: never;
  NavLink: ElementType;
}

const ariaIds = {
  label: 'user-menu-label',
  toggleButton: 'user-menu-toggle-button',
};

export const UserMenu = ({
  email,
  role,
  logOutHref,
  logOutText,
  NavLink,
}: UserMenuProps) => {
  const [open, setOpen] = useState(false);

  const {
    setFloating,
    setReference,
    getFloating,
    constrainStyles,
    floatingStyles,
  } = usePositionedPopup<HTMLButtonElement, HTMLDialogElement>({
    open,
    placement: 'bottom-end',
  });

  useEffect(() => {
    if (open) getFloating()?.showModal();
  }, [getFloating, open]);

  const closeOnOutsideClick:
    | MouseEventHandler<HTMLDialogElement>
    | undefined = ({ currentTarget, target }) => {
    const menu = getFloating();

    if (!menu) return;

    const hasClickedOutsideModal = target === currentTarget && target === menu;

    if (hasClickedOutsideModal) {
      menu.close();
    }
  };

  return (
    <>
      <Text
        paragraph
        data-testid={TEST_IDS.EMAIL}
        id={ariaIds.label}
        htmlFor={ariaIds.toggleButton}
      >
        {email}
      </Text>
      <Button
        onClick={() => setOpen(true)}
        variant="icon"
        ref={setReference}
        id={ariaIds.toggleButton}
        aria-labelledby={ariaIds.label}
        Icon={ChevronDownIcon}
        iconColor="neutral500"
      />

      <dialog
        className={styles.dialog}
        style={floatingStyles}
        ref={setFloating}
        onClick={closeOnOutsideClick}
        onClose={() => {
          setOpen(false);
        }}
        // dialog does not have accessible role in jsdom
        data-testid={TEST_IDS.MENU}
      >
        <GrandEntrance
          show={open}
          config={
            open
              ? {
                  mass: 1,
                  tension: 260,
                  friction: 10,
                  precision: 0.01,
                  clamp: true,
                  velocity: 0.002, // seconds
                }
              : {
                  duration: 0,
                }
          }
        >
          <Box
            className={classFilter([styles.menu, constrainStyles])}
            variant="initial"
            padding={{ top: 'baseNeg2' }}
          >
            <Card
              variant="lightShadow"
              alignContent={{ x: 'left' }}
              role="menu"
              spacing="basePos4"
              padding={{ x: 'basePos3', top: 'basePos3', bottom: 'basePos4' }}
            >
              <Box spacing="baseNeg4">
                <Text paragraph data-testid={TEST_IDS.CARD_EMAIL}>
                  {email}
                </Text>
                <Text paragraph data-testid={TEST_IDS.CARD_ROLE}>
                  {role}
                </Text>
              </Box>

              <Link to={logOutHref} NavLink={NavLink}>
                {logOutText}
              </Link>
            </Card>
          </Box>
        </GrandEntrance>
      </dialog>
    </>
  );
};
